import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV = [
  { label: "Projects", to: "/projects" },
  { label: "Services", to: "/services" },
  { label: "Process", to: "/process" },
  { label: "Studio", to: "/studio" },
];

type Props = { transparentOnTop?: boolean; hidden?: boolean };

export default function SiteNav({ transparentOnTop = false, hidden = false }: Props) {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(!transparentOnTop);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!transparentOnTop) { setSolid(true); return; }
    const on = () => setSolid(window.scrollY > 40);
    window.addEventListener("scroll", on, { passive: true });
    on();
    return () => window.removeEventListener("scroll", on);
  }, [transparentOnTop]);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-warm-bg border-b hairline" : "bg-transparent"
      }`}
      style={{
        transform: hidden ? "translate3d(0, -100%, 0)" : "translate3d(0, 0, 0)",
        transition: "transform 500ms cubic-bezier(0.22, 1, 0.36, 1), background-color 500ms, border-color 500ms",
        pointerEvents: hidden ? "none" : "auto",
      }}
    >
      <div className="mx-auto max-w-page px-5 md:px-12 lg:px-16">
        <nav className="flex items-center justify-between h-[72px] md:h-20">
          <Link to="/" className="flex items-baseline gap-[3px] cursor-pointer">
            <span className={`font-serif-display font-normal text-[22px] md:text-[24px] leading-none tracking-[-0.01em] transition-colors ${solid ? "text-ink" : "text-white"}`}>Atelier</span>
            <span className={`font-serif-display italic font-light text-[22px] md:text-[24px] leading-none tracking-[-0.01em] transition-colors ${solid ? "text-accent" : "text-white/85"}`}>Ridge</span>
          </Link>
          <ul className="hidden md:flex items-center gap-10">
            {NAV.map(i => (
              <li key={i.to}>
                <Link to={i.to} className={`text-[12px] tracking-[0.1em] uppercase transition-colors cursor-pointer ${
                  pathname.startsWith(i.to)
                    ? "text-accent"
                    : solid
                      ? "text-ink hover:text-accent"
                      : "text-white hover:text-white/70"
                }`}>{i.label}</Link>
              </li>
            ))}
          </ul>
          <Link to="/contact" className={`hidden md:inline-flex arrow-link text-[12px] tracking-[0.1em] uppercase transition-colors whitespace-nowrap cursor-pointer ${solid ? "text-ink hover:text-accent" : "text-white hover:text-white/70"}`}>
            Start a Project
            <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
          </Link>
          <button onClick={() => setOpen(v => !v)}
                  className={`md:hidden w-11 h-11 flex items-center justify-center cursor-pointer transition-colors ${solid ? "text-ink" : "text-white"}`}
                  aria-label="Menu">
            <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-[22px] w-6 h-6 flex items-center justify-center`} />
          </button>
        </nav>
      </div>
      {open && (
        <div className="md:hidden bg-warm-bg border-t hairline mobile-menu-enter">
          <div className="px-5 py-6 flex flex-col gap-5">
            {NAV.map(i => (
              <Link key={i.to} to={i.to} className="text-[16px] cursor-pointer">{i.label}</Link>
            ))}
            <Link to="/contact" className="arrow-link text-[16px] text-accent cursor-pointer">
              Start a Project
              <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}